import React, { useEffect, useState, useContext } from 'react';
import { IRootState } from 'src/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { Url } from 'src/api';
import Card from 'src/components/common/Card.redesign';
import VisibilityLoader from 'src/components/common/VisibilityLoader';
import useFetch from 'use-http';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import SectionCard from './SectionCard';
import { push } from 'connected-react-router';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { LandSystem } from 'src/models/LandSystem';
import tagsToString from 'src/components/common/UtilityFunctions';

interface ITownshipCardProps {
    townshipId: number;
    isActive: boolean;
    onTownshipSelected: (townshipId: number) => void;
    onSectionSelected: (sectionNumber: number) => void;
    reloadTownships: (townshipId: number) => void;
    parentSelectedSection?: number;
    landSystem: LandSystem;
}

const TownshipCard: React.FC<ITownshipCardProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const dialogContext = useContext<dialogCreators>(DialogContext);
    const [township, setTownship] = useState(undefined);
    const [sectionIds, setSectionIds] = useState([]);
    const [selectedSection, setSelectedSection] = useState(0);
    const [dataLoaded, setDataLoaded] = useState(false);
    const { get, response } = useFetch();
    const dispatch = useDispatch();

    const loadData = async (isVisible: boolean) => {
        if (isVisible && !township) {
            const result = await get(Url.project.getTownship(appContext, props.townshipId));

            if (response.ok) {
                setTownship(result);
            }
        }
    };

    const loadSections = async () => {
        const data = await get(`${Url.project.getSectionsForTownship(appContext, props.townshipId)}?select=id`);

        if (response.ok) {
            setDataLoaded(true);
            setSectionIds(data);
            if (appContext.sectionNumber) {
                const idx = (data as number[]).indexOf(appContext.sectionNumber);
                setSelectedSection(data[idx]);
                props.onSectionSelected(data[idx]);
            } else {
                setSelectedSection(data[0]);
                props.onSectionSelected(data[0]);
            }
        }
    };

    const reloadData = async (townshipId: number) => {
        const result = await get(Url.project.getTownship(appContext, props.townshipId));

        if (response.ok) {
            setTownship(result);
        }
        props.reloadTownships(townshipId);
    };

    useEffect(() => {
        if (props.isActive) {
            if (sectionIds.length <= 0) {
                loadSections();
            } else {
                props.onSectionSelected(selectedSection);
            }
        }
    }, [props.isActive]);

    useEffect(() => {
        if (props.parentSelectedSection) {
            setSelectedSection(props.parentSelectedSection);
            onSectionSelected(props.parentSelectedSection);
        }
    }, [props.parentSelectedSection]);

    const onSectionSelected = (sectionNumber: number) => {
        let newPath = '';
        const secIndex = window.location.pathname.indexOf('/sections');
        if (secIndex > 0) {
            newPath = window.location.pathname.substring(0, secIndex) + `/sections/${sectionNumber}/tracts`;
        } else {
            newPath = window.location.pathname.replace('/tracts', `/sections/${sectionNumber}/tracts`);
        }

        setSelectedSection(sectionNumber);
        props.onSectionSelected(sectionNumber);
        dispatch(push(newPath));
    };

    const reloadSections = async (sectionNumber: number) => {
        const data = await get(`${Url.project.getSectionsForTownship(appContext, props.townshipId)}?select=id`);

        if (response.ok) {
            setSectionIds(data);
            setSelectedSection(sectionNumber);
            props.onSectionSelected(sectionNumber);
        }
    };

    const sectionsPanel = () => {
        let sectionContent: React.ReactElement = undefined;

        if (!dataLoaded) {
            sectionContent = <div>Loading...</div>;
        } else if (sectionIds.length === 0) {
            sectionContent = (
                <div
                    style={{
                        padding: '1rem',
                        height: '5rem',
                        fontWeight: 'bold',
                        border: '2px solid red',
                        fontSize: '1.1em',
                        verticalAlign: 'center ',
                    }}
                >
                    <div>
                        No Sections
                        <Button
                            style={{ marginLeft: '1rem' }}
                            size="small"
                            intent="primary"
                            text="Create Section"
                            onClick={() =>
                                dialogContext.project.addUpdateDeleteSection(props.townshipId, 0, 'create', props.landSystem, reloadSections)
                            }
                        />
                    </div>
                </div>
            );
        } else {
            sectionContent = (
                <>
                    {sectionIds?.map((id) => (
                        <SectionCard
                            key={id}
                            sectionNumber={id}
                            townshipId={props.townshipId}
                            onSectionSelected={onSectionSelected}
                            isActive={selectedSection === id}
                            landSystem={props.landSystem}
                        />
                    ))}
                </>
            );
        }

        return (
            <div>
                <div style={{ width: '100%', padding: '1rem 1rem' }}>
                    <span 
                        style={{ visibility: (township?.tags?.length ? township?.tags?.length : 0) < 1 ? 'hidden' : 'visible',
                        fontWeight: 'bold', display: 'inline-block', marginRight: '.6rem', whiteSpace: 'nowrap' }}
                    >Tags:
                    </span>
                    <span>
                        {tagsToString(township?.tags)}
                    </span>
                </div>
                <div style={{ border: '1px solid #ccc', maxHeight: '80rem', overflow: 'hidden', overflowY: 'auto', marginTop: '.5rem' }}>
                    {sectionContent}
                </div>
            </div>
        );
    };

    const content = () => {
        const townshipLabels = [];

        if (!township) {
            return <div>Loading...</div>;
        } else {
            if (township.block) {
                townshipLabels.push(`Block ${township.block}`);
            }

            if (township.townshipNumber) {
                townshipLabels.push(`T${township.townshipNumber}${township.townshipCardinal} `);
            }

            if (township.rangeNumber) {
                townshipLabels.push(`R${township.rangeNumber}${township.rangeCardinal}`);
            }

            return (
                <div>
                    <span className="title">{townshipLabels.join(' - ')}</span>
                    {props.isActive && sectionsPanel()}
                </div>
            );
        }
    };

    const actions: ContextMenuAction[] = [
        {
            text: 'Create Section',
            action: () => dialogContext.project.addUpdateDeleteSection(township.id, 0, 'create', props.landSystem, reloadSections),
        },
        { text: 'Modify Township', action: () => dialogContext.project.addUpdateDeleteTownship(township.id, 'edit', props.landSystem, reloadData) },
    ];

    return (
        <Card
            newContext={true}
            showMenu={true}
            contextActions={props.isActive && actions}
            onClick={() => props.onTownshipSelected(props.townshipId)}
            rightBadgePanel={
                township && (
                    <span style={{ position: 'absolute', right: '3rem', fontSize: '.9em', fontWeight: 'bold', color: '#999' }}>
                        {township.sectionCount} sections
                    </span>
                )
            }
        >
            <VisibilityLoader onVisibilityChanged={loadData}>
                <div style={{ padding: '1rem', position: 'relative' }}>{content()}</div>
            </VisibilityLoader>
        </Card>
    );
};

export default TownshipCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/TownshipCard.tsx